/* 208 §15 — THE BELL TELLS THE MAC WHAT IS NEW, NOT WHAT IS UNREAD.

   The rule the page applies each time its unread bell items change (App.tsx,
   the effect keyed by their ids): every unread item is SEEN once; the first
   time the bell has loaded — the page's data in, its persisted rows answered —
   is the baseline, and what the page learns within `settleMs` of it was unread
   when the page opened: it stays in the bell. After that, an item seen for the
   first time has ARRIVED, and it is the one the Mac hears of (the page's own
   gates then: the permission, the working hours). A page used to send every
   unread item it had not sent yet — and that set is empty with every page, so
   each page that opened sent the whole backlog at once.

   Pure: the page holds `seen` (a Set it keeps for its life) and `firstLoadAt`;
   the recording (recordings/208-bell-to-mac) runs this function. */
export interface BellToMac<T> {
  /** The unread bell items now. */
  unread: readonly T[];
  /** The ids this page has already seen — added to here. */
  seen: Set<unknown>;
  /** When the bell first loaded on this page, or null before it has. */
  firstLoadAt: number | null;
  now: number;
  settleMs: number;
}

/** The items that ARRIVE now — the ones the Mac hears of — and the baseline, set on the first call. */
export function bellToMac<T extends { id: unknown }>(s: Readonly<BellToMac<T>>): { arrived: T[]; firstLoadAt: number } {
  const fresh = s.unread.filter((n) => !s.seen.has(n.id));
  for (const n of fresh) s.seen.add(n.id);
  const firstLoadAt = s.firstLoadAt ?? s.now;
  return { arrived: s.now - firstLoadAt < s.settleMs ? [] : fresh, firstLoadAt };
}
